import { z } from "zod";
export const protocolVersion = "1.0";
export const userStatusSchema = z.enum(["ACTIVE", "DISABLED", "LOCKED"]);
export const roleNameSchema = z.enum(["SUPER_ADMIN", "ADMIN", "OPERATOR", "VIEWER"]);
export const deviceStatusSchema = z.enum([
    "OFFLINE",
    "AVAILABLE",
    "RESERVED",
    "CONNECTING",
    "IN_USE",
    "DEGRADED",
    "MAINTENANCE",
]);
export const remoteSessionStateSchema = z.enum([
    "RESERVED",
    "CONNECTING",
    "ACTIVE",
    "RECONNECTING",
    "DEGRADED",
    "ENDED",
    "FAILED",
]);
export const sessionModeSchema = z.enum(["VIEW", "CONTROL"]);
export const connectionModeSchema = z.enum(["P2P", "TURN", "UNKNOWN"]);
export const agentStatusSchema = z.enum(["ENROLLING", "ONLINE", "OFFLINE", "REVOKED", "DEGRADED"]);
export const permissionSchema = z.enum([
    "device:list",
    "device:view",
    "device:control",
    "device:clipboard",
    "device:screenshot",
    "device:record",
    "device:takeover",
    "device:manage",
    "user:manage",
    "audit:view",
    "agent:manage",
    "settings:manage",
]);
export const errorEnvelopeSchema = z.object({
    error: z.object({
        code: z.string().min(1).max(80),
        message: z.string().min(1).max(500),
        details: z.unknown().optional(),
    }),
    correlationId: z.uuid(),
});
export const healthResponseSchema = z.object({
    status: z.enum(["ok", "degraded", "unavailable"]),
    version: z.string().min(1),
    timestamp: z.iso.datetime(),
    checks: z.record(z.string(), z.enum(["ok", "unavailable"])).optional(),
});
export const deviceSummarySchema = z.object({
    id: z.uuid(),
    alias: z.string().min(1).max(64),
    group: z.string().max(64).nullable(),
    model: z.string().max(128).nullable(),
    manufacturer: z.string().max(128).nullable(),
    androidVersion: z.string().max(32).nullable(),
    status: deviceStatusSchema,
    batteryLevel: z.number().int().min(0).max(100).nullable(),
    agentName: z.string().min(1).max(128),
    site: z.string().min(1).max(128),
    lastSeenAt: z.iso.datetime().nullable(),
    capabilities: z.record(z.string(), z.boolean()),
});
export const deviceListQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
    status: deviceStatusSchema.optional(),
    group: z.string().trim().max(64).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const deviceListResponseSchema = z.object({
    items: z.array(deviceSummarySchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
});
export const deviceUpdateSchema = z
    .object({
    alias: z.string().trim().min(1).max(64).optional(),
    group: z.string().trim().max(64).nullable().optional(),
    notes: z.string().trim().max(1_000).nullable().optional(),
    maintenance: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: "At least one device field is required",
});
export const agentEnrollmentTokenRequestSchema = z.object({
    expiresInSeconds: z.number().int().min(60).max(3_600).optional(),
});
export const agentEnrollmentTokenResponseSchema = z.object({
    token: z.string().min(32).max(256),
    expiresAt: z.iso.datetime(),
});
export const agentEnrollRequestSchema = z.object({
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
    name: z.string().trim().min(1).max(128),
    site: z.string().trim().min(1).max(128),
    version: z.string().trim().min(1).max(64),
    publicKeyPem: z
        .string()
        .min(80)
        .max(4_096)
        .refine((value) => value.includes("BEGIN PUBLIC KEY"), {
        message: "Expected a PEM encoded public key",
    }),
    publicKeyFingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
});
export const agentEnrollResponseSchema = z.object({
    agentId: z.uuid(),
    credentialVersion: z.number().int().min(1),
    protocolVersion: z.literal(protocolVersion),
    enrolledAt: z.iso.datetime(),
});
export const agentSummarySchema = z.object({
    id: z.uuid(),
    name: z.string().min(1).max(128),
    site: z.string().min(1).max(128),
    status: agentStatusSchema,
    version: z.string().max(64).nullable(),
    credentialVersion: z.number().int().min(1),
    enrolledAt: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    lastSeenAt: z.iso.datetime().nullable(),
    deviceCount: z.number().int().min(0),
});
export const agentHandshakeSchema = z.object({
    protocolVersion: z.literal(protocolVersion),
    agentId: z.uuid(),
    credentialVersion: z.number().int().min(1),
    timestamp: z.number().int().positive(),
    nonce: z.string().min(32).max(128),
    signature: z.string().min(64).max(512),
});
export const agentHelloPayloadSchema = z.object({
    version: z.string().min(1).max(64),
    name: z.string().min(1).max(128),
    site: z.string().min(1).max(128),
    platform: z.string().min(1).max(64),
    architecture: z.string().min(1).max(32),
});
export const agentHeartbeatPayloadSchema = z.object({
    processUptimeSeconds: z.number().int().min(0),
    deviceCount: z.number().int().min(0).max(1_000),
    activeSessionCount: z.number().int().min(0).max(100),
});
export const agentDeviceInventoryItemSchema = z.object({
    stableKeyHash: z.string().regex(/^[a-f0-9]{64}$/u),
    runtimeEndpoint: z.string().min(1).max(256),
    model: z.string().max(128).nullable(),
    manufacturer: z.string().max(128).nullable(),
    androidVersion: z.string().max(32).nullable(),
    batteryLevel: z.number().int().min(0).max(100).nullable(),
    status: z.enum(["AVAILABLE", "OFFLINE", "DEGRADED"]),
    capabilities: z.record(z.string(), z.boolean()),
    lastErrorCode: z.string().max(80).nullable(),
});
export const agentDeviceSnapshotPayloadSchema = z.object({
    sequence: z.number().int().min(1),
    devices: z.array(agentDeviceInventoryItemSchema).max(100),
});
export const iceServerSchema = z.object({
    urls: z.array(z.string().min(1).max(512)).min(1).max(8),
    username: z.string().max(512).optional(),
    credential: z.string().max(512).optional(),
});
export const agentSessionStartPayloadSchema = z.object({
    sessionId: z.uuid(),
    deviceId: z.uuid(),
    deviceStableKeyHash: z.string().regex(/^[a-f0-9]{64}$/u),
    mode: sessionModeSchema,
    sessionNonce: z.string().min(32).max(512),
    iceServers: z.array(iceServerSchema).max(4),
    forceRelay: z.boolean().default(false),
});
export const agentSessionStopPayloadSchema = z.object({
    sessionId: z.uuid(),
    reason: z.string().min(1).max(80),
});
export const agentSessionStatePayloadSchema = z.object({
    sessionId: z.uuid(),
    state: z.enum(["STARTED", "FAILED", "STOPPED"]),
    width: z.number().int().positive().max(8_192).optional(),
    height: z.number().int().positive().max(8_192).optional(),
    errorCode: z.string().max(80).optional(),
});
export const controlMessageSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("authorize"),
        nonce: z.string().min(32).max(512),
    }),
    z.object({
        type: z.literal("pointer"),
        action: z.enum(["down", "move", "up"]),
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        pointerId: z.number().int().min(0).max(9).default(0),
    }),
    z.object({
        type: z.literal("key"),
        key: z.enum([
            "back",
            "home",
            "appSwitch",
            "enter",
            "backspace",
            "delete",
            "tab",
            "escape",
            "arrowUp",
            "arrowDown",
            "arrowLeft",
            "arrowRight",
        ]),
    }),
    z.object({
        type: z.literal("text"),
        text: z.string().min(1).max(256),
    }),
    z.object({
        type: z.literal("rotate"),
    }),
]);
export const sessionRequestSchema = z.object({
    mode: sessionModeSchema,
    forceRelay: z.boolean().default(false),
});
export const sessionResponseSchema = z.object({
    id: z.uuid(),
    deviceId: z.uuid(),
    mode: sessionModeSchema,
    state: remoteSessionStateSchema,
    expiresAt: z.iso.datetime(),
    signalingNonce: z.string().min(32).max(512),
});
export const realtimeEventNameSchema = z.enum([
    "session:join",
    "session:leave",
    "session:heartbeat",
    "session:state",
    "session:error",
    "webrtc:offer",
    "webrtc:answer",
    "webrtc:ice-candidate",
    "webrtc:renegotiate",
    "quality:update",
    "controller:revoked",
    "agent:hello",
    "agent:heartbeat",
    "agent:health",
    "device:snapshot",
    "device:upsert",
    "device:status",
    "session:start",
    "session:stop",
    "session:started",
    "session:failed",
]);
export const realtimeEnvelopeSchema = z.object({
    protocolVersion: z.literal(protocolVersion),
    eventId: z.uuid(),
    timestamp: z.iso.datetime(),
    correlationId: z.uuid(),
    sessionId: z.uuid().optional(),
    payload: z.unknown(),
});
export const iceCandidateSchema = z.object({
    candidate: z.string().max(8192),
    sdpMid: z.string().max(128).nullable(),
    sdpMLineIndex: z.number().int().min(0).max(32).nullable(),
    usernameFragment: z.string().max(256).nullable().optional(),
});
export const sessionDescriptionSchema = z.object({
    type: z.enum(["offer", "answer"]),
    sdp: z.string().min(1).max(262_144),
});
export const qualityUpdateSchema = z.object({
    connectionMode: connectionModeSchema,
    rttMs: z.number().min(0).max(120_000).nullable(),
    packetLossPercent: z.number().min(0).max(100).nullable(),
    jitterMs: z.number().min(0).max(120_000).nullable(),
    bitrateBps: z.number().int().min(0).max(100_000_000).nullable(),
});
const allowedTransitions = Object.freeze({
    RESERVED: ["CONNECTING", "ENDED", "FAILED"],
    CONNECTING: ["ACTIVE", "DEGRADED", "ENDED", "FAILED"],
    ACTIVE: ["RECONNECTING", "DEGRADED", "ENDED", "FAILED"],
    RECONNECTING: ["ACTIVE", "DEGRADED", "ENDED", "FAILED"],
    DEGRADED: ["RECONNECTING", "ENDED", "FAILED"],
    ENDED: [],
    FAILED: [],
});
export function canTransitionSession(from, to) {
    return allowedTransitions[from].includes(to);
}
function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
}
export function mapPointerToDevice(point, viewport, rotation = 0) {
    const values = [
        point.x,
        point.y,
        viewport.elementWidth,
        viewport.elementHeight,
        viewport.videoWidth,
        viewport.videoHeight,
    ];
    if (values.some((value) => !Number.isFinite(value))) {
        throw new TypeError("Coordinates and dimensions must be finite");
    }
    if (viewport.elementWidth <= 0 ||
        viewport.elementHeight <= 0 ||
        viewport.videoWidth <= 0 ||
        viewport.videoHeight <= 0) {
        throw new RangeError("Viewport dimensions must be positive");
    }
    const scale = Math.min(viewport.elementWidth / viewport.videoWidth, viewport.elementHeight / viewport.videoHeight);
    const renderedWidth = viewport.videoWidth * scale;
    const renderedHeight = viewport.videoHeight * scale;
    const offsetX = (viewport.elementWidth - renderedWidth) / 2;
    const offsetY = (viewport.elementHeight - renderedHeight) / 2;
    const normalizedX = (point.x - offsetX) / renderedWidth;
    const normalizedY = (point.y - offsetY) / renderedHeight;
    const insideVideo = normalizedX >= 0 && normalizedX <= 1 && normalizedY >= 0 && normalizedY <= 1;
    const x = clamp(normalizedX, 0, 1);
    const y = clamp(normalizedY, 0, 1);
    const rotated = (() => {
        switch (rotation) {
            case 0:
                return { x, y };
            case 90:
                return { x: y, y: 1 - x };
            case 180:
                return { x: 1 - x, y: 1 - y };
            case 270:
                return { x: 1 - y, y: x };
        }
    })();
    return {
        x: Math.round(rotated.x * (viewport.videoWidth - 1)),
        y: Math.round(rotated.y * (viewport.videoHeight - 1)),
        insideVideo,
    };
}
//# sourceMappingURL=index.js.map